import { useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { Activity, Copy, Eye, EyeOff, GripVertical, List, Lock, RotateCcw, Settings2, Trash2, Trophy } from 'lucide-react'
import { Button, Field, IconButton, Input, Select, Switch } from '@/components/common'
import { DeviceStage } from './NativePreview'
import { GamePagePreview } from './GamePagePreview'
import { EventLayoutCopyDialog } from './EventLayoutCopyDialog'
import { NativeClipboardControls } from './NativeClipboardControls'
import { CATEGORIES, experienceTargetOptions, SECTION_NAMES } from './experienceModel'
import { eventPreviewTabs, nativeCategoryTitle } from './eventMarketPreviewModel'
import { broaderGamePages, exampleGames, gamePageChanges, gamePageLabel, gamePageState, resolveGamePage, selectedGamePage, setGamePageField } from './gamePageModel'
import { crossedMidpoint } from './nativeDnd'
import { PREVIEW_CATALOG } from './nativePreviewModel'
import styles from './nativeSportsbook.module.css'

const SECTION_DRAG_TYPE = 'native-game-section'
const TAB_DRAG_TYPE = 'native-game-tab'
const SECTION_ICONS = { event_header: Trophy, event_probability: Activity, event_markets: List }
const REQUIRED = new Set(['event_header', 'event_markets'])
// Sections the app no longer draws. The manifest keeps them; the editor doesn't offer them.
const RETIRED = new Set(['event_insights'])
const HEADER_STYLES = { editorial: 'Editorial matchup', compact: 'Compact scoreboard' }
const EXPANDED = { all: 'All markets', first: 'First market', none: 'None' }
const MARKET_STYLES = { automatic: 'Automatic', grid: 'Grid' }

// A game page edits like Home: its sections on the left, the phone in the
// middle, the selected section's settings on the right.
export function GamePageWorkspace({ workspace, organization, device, appearance, disabled }) {
  const [copyOpen, setCopyOpen] = useState(false)
  const experience = workspace.experience
  const catalog = workspace.previewCatalog || PREVIEW_CATALOG
  const selection = workspace.editorSelection
  const state = gamePageState(selection)
  const template = selectedGamePage(experience, selection)
  const games = exampleGames(template, catalog, state)
  const event = selection.eventId ? games.find((item) => item.id === selection.eventId) : games[0]
  const presentation = resolveGamePage(experience, template, event, state)
  const overrides = gamePageChanges(experience, template, event, state)
  const editable = presentation.inheritanceComplete
  const section = editable ? selection.section : null
  const setField = (field, value) => workspace.updateExperience(setGamePageField(experience, template.id, state, field, value))
  const select = (patch) => workspace.setEditorSelection(patch)
  const resetLabel = state === 'in_play' ? 'Same as before start' : template.scope.kind === 'default' ? 'Use the app default' : `Use ${gamePageLabel(broaderGamePages(experience, template).at(-1) ?? template)} setting`
  const field = { overrides, setField, resetLabel, disabled }
  return <div className={styles.workspace}>
    <SectionsPanel {...{ workspace, organization, experience, catalog, template, event, presentation, editable, section, setField, select, disabled }} />
    <div className={styles.canvasArea}>
      <DeviceStage>
        <GamePagePreview key={`${template.id}:${state}:${event?.id}:${presentation.initial_category}:${presentation.initially_expanded_markets}`} manifest={workspace.document} event={editable ? event : null} freshness={catalog.freshness} presentation={presentation} device={device} appearance={appearance} selectedSection={section} onSelect={(type) => select({ section: type })} interactive={!disabled} />
      </DeviceStage>
    </div>
    {section ? <SectionInspector {...{ section, presentation, event, field, state, onBack: () => select({ section: null }) }} />
      : <PageInspector {...{ experience, catalog, template, state, editable, disabled, onCopy: () => setCopyOpen(true), workspace }} />}
    {copyOpen && !disabled ? <EventLayoutCopyDialog {...{ experience, template, catalog }} onClose={() => setCopyOpen(false)} onCopy={({ experience: next, template: copy }) => { workspace.updateExperience(next); select({ templateId: copy.id, eventId: null, section: null }) }} /> : null}
  </div>
}

function SectionsPanel({ workspace, organization, experience, catalog, template, event, presentation, editable, section, setField, select, disabled }) {
  const sections = presentation.sections
  const offered = sections.filter((item) => !RETIRED.has(item.type))
  const visible = offered.filter((item) => item.enabled).length
  const moveSection = (from, to) => {
    if (from === 0 || to < 1 || to >= sections.length || from === to) return false
    const next = [...sections]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    setField('sections', next)
    return true
  }
  return <section className={`${styles.panel} ${styles.layersArea}`} aria-label="Sections">
    <header className={styles.panelHeader}><h2>Sections</h2>{editable ? <span>{visible} of {offered.length} visible</span> : null}</header>
    <ol className={styles.layers}>
      <li className={styles.layer} data-selected={section ? 'false' : 'true'}>
        <span className={styles.layerHandle} aria-hidden="true"><Settings2 size={14} /></span>
        <button type="button" className={styles.layerSelect} aria-current={section ? undefined : 'true'} onClick={() => select({ section: null })}>
          <span className={styles.layerText}><strong>Page settings</strong><small>{appliesTo(template, catalog)}</small></span>
        </button>
      </li>
      {editable ? sections.map((item, index) => RETIRED.has(item.type) ? null : <SectionRow key={item.id} item={item} index={index} selected={section === item.type} disabled={disabled}
        onSelect={() => select({ section: item.type })} onMoveTo={moveSection}
        onToggle={() => setField('sections', sections.map((candidate) => candidate.id === item.id ? { ...candidate, enabled: !candidate.enabled } : candidate))}>
        {item.type === 'event_markets' ? <TabRows event={event} presentation={presentation} disabled={disabled} setField={setField} onSelect={() => select({ section: 'event_markets' })} /> : null}
      </SectionRow>) : <li className={styles.inspectorHint}>No games for this page in the current data.</li>}
    </ol>
    <footer className={styles.panelFooter}><NativeClipboardControls {...{ workspace, organization, experience, catalog, disabled }} /></footer>
  </section>
}

function appliesTo(template, catalog) {
  if (template.scope.kind === 'default') return 'Every game'
  const label = experienceTargetOptions(catalog, template.scope.kind).find((option) => option.value === template.scope.value)?.label ?? template.scope.value
  return template.scope.kind === 'event' ? label : `${label} games`
}

function useSortableRow(type, index, canMove, onMoveTo) {
  const ref = useRef(null)
  const [{ isDragging }, drag] = useDrag(() => ({ type, item: { index }, canDrag: canMove, collect: (monitor) => ({ isDragging: monitor.isDragging() }) }), [type, index, canMove])
  const [, drop] = useDrop(() => ({
    accept: type,
    hover: (item, monitor) => {
      if (!canMove || item.index === index || !crossedMidpoint(ref.current, monitor, item.index, index)) return
      if (onMoveTo(item.index, index)) item.index = index
    },
  }), [type, index, canMove, onMoveTo])
  drag(drop(ref))
  return { ref, isDragging }
}

function SectionRow({ item, index, selected, disabled, onSelect, onMoveTo, onToggle, children }) {
  const locked = item.type === 'event_header'
  const { ref, isDragging } = useSortableRow(SECTION_DRAG_TYPE, index, !disabled && !locked, onMoveTo)
  const Icon = SECTION_ICONS[item.type] ?? List
  const name = SECTION_NAMES[item.type]
  return <li className={styles.layerGroup} data-dragging={isDragging ? 'true' : 'false'}>
    <div ref={ref} className={styles.layer} data-selected={selected ? 'true' : 'false'} data-hidden={item.enabled ? 'false' : 'true'} data-locked={locked ? 'true' : 'false'}>
      <span className={styles.layerHandle} aria-hidden="true">{locked ? <Lock size={13} /> : <GripVertical size={14} />}</span>
      <button type="button" className={styles.layerSelect} aria-current={selected ? 'true' : undefined} onClick={onSelect}>
        <Icon size={14} className={styles.layerIcon} aria-hidden="true" />
        <span className={styles.layerText}><strong>{name}</strong></span>
      </button>
      <span className={styles.layerActions}>
        {REQUIRED.has(item.type) ? <span className={styles.layerRequired}>Required</span>
          : <IconButton variant="ghost" className={styles.rowIcon} label={item.enabled ? `Hide ${name}` : `Show ${name}`} disabled={disabled} onClick={onToggle}>{item.enabled ? <Eye size={14} /> : <EyeOff size={14} />}</IconButton>}
      </span>
    </div>
    {children}
  </li>
}

// The game's market tabs in the order the page shows them. Dragging one sets
// the page's tab order; the tab the page opens on is marked.
function TabRows({ event, presentation, disabled, setField, onSelect }) {
  const available = event ? eventPreviewTabs(event).map((tab) => tab.key) : []
  const priority = presentation.category_order
  const ordered = available.map((key, index) => ({ key, index })).sort((a, b) => {
    const left = priority.indexOf(a.key)
    const right = priority.indexOf(b.key)
    return (left < 0 ? Infinity : left) - (right < 0 ? Infinity : right) || a.index - b.index
  }).map((item) => item.key)
  if (!ordered.length) return null
  const opening = ordered.includes(presentation.initial_category) ? presentation.initial_category : ordered[0]
  const moveTab = (from, to) => {
    if (from === to || to < 0 || to >= ordered.length) return false
    const next = [...ordered]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    setField('category_order', [...next, ...priority.filter((key) => !next.includes(key))])
    return true
  }
  return <ol className={styles.cardLayers} aria-label="Market tabs">
    {ordered.map((key, index) => <TabRow key={key} label={nativeCategoryTitle(key, event.sport)} opening={key === opening} index={index} disabled={disabled} onMoveTo={moveTab} onSelect={onSelect} />)}
  </ol>
}

function TabRow({ label, opening, index, disabled, onMoveTo, onSelect }) {
  const { ref, isDragging } = useSortableRow(TAB_DRAG_TYPE, index, !disabled, onMoveTo)
  return <li ref={ref} className={`${styles.layer} ${styles.cardLayer}`} data-dragging={isDragging ? 'true' : 'false'}>
    <span className={styles.layerHandle} aria-hidden="true"><GripVertical size={13} /></span>
    <button type="button" className={styles.layerSelect} onClick={onSelect}>
      <span className={styles.layerText}><strong>{label}</strong>{opening ? <small>Opens here</small> : null}</span>
    </button>
  </li>
}

function PageInspector({ experience, catalog, template, state, editable, disabled, onCopy, workspace }) {
  const broader = broaderGamePages(experience, template)
  const remove = () => {
    workspace.updateExperience({ ...experience, event_templates: experience.event_templates.filter((item) => item.id !== template.id) })
    workspace.setEditorSelection({ templateId: null, eventId: null, section: null })
  }
  return <section className={`${styles.panel} ${styles.inspectorArea}`} aria-label="Page settings">
    <header className={styles.inspectorHeader}>
      <div className={styles.inspectorTitle}><span className={styles.eyebrow}>{state === 'in_play' ? 'Game page · Live' : 'Game page'}</span><h2>{gamePageLabel(template)}</h2></div>
      <div className={styles.inspectorActions}>
        <IconButton className={styles.rowIcon} label="Duplicate page" disabled={disabled || experience.event_templates.length >= 32} onClick={onCopy}><Copy size={14} /></IconButton>
        {template.scope.kind !== 'default' ? <IconButton variant="danger" className={styles.rowIcon} label="Remove page" disabled={disabled} onClick={remove}><Trash2 size={14} /></IconButton> : null}
      </div>
    </header>
    <div className={styles.inspectorFields}>
      <Field label="Applies to"><Input value={appliesTo(template, catalog)} readOnly /></Field>
      {template.scope.kind !== 'default' ? <Field label="Name"><Input disabled={disabled} maxLength={80} value={template.name} onChange={(change) => workspace.updateExperience({ ...experience, event_templates: experience.event_templates.map((item) => item.id === template.id ? { ...item, name: change.target.value } : item) })} /></Field> : null}
      {broader.length ? <p className={styles.inspectorHint}>Starts from {broader.map(gamePageLabel).join(' › ')}.</p> : null}
      {!editable ? <p className={styles.inspectorHint}>No games for this page in the current data.</p> : <p className={styles.inspectorHint}>Select a section to change it.</p>}
    </div>
  </section>
}

function SectionInspector({ section, presentation, event, field, state, onBack }) {
  const name = SECTION_NAMES[section]
  const current = presentation.sections.find((item) => item.type === section)
  const tabs = event ? eventPreviewTabs(event).map((tab) => tab.key) : []
  return <section className={`${styles.panel} ${styles.inspectorArea}`} aria-label={`${name} settings`}>
    <header className={styles.inspectorHeader}>
      <div className={styles.inspectorTitle}>
        <button type="button" className={styles.inspectorBack} onClick={onBack}>Page settings</button>
        <span className={styles.eyebrow}>{state === 'in_play' ? 'Section · Live' : 'Section'}</span>
        <h2>{name}</h2>
      </div>
    </header>
    <div className={styles.inspectorFields}>
      {section === 'event_header' ? <Choice label="Style" name="header_style" values={HEADER_STYLES} {...{ presentation, field }} /> : null}
      {section === 'event_markets' ? <>
        <Choice label="Opens on" name="initial_category" values={Object.fromEntries(Object.entries(CATEGORIES).filter(([key]) => !tabs.length || tabs.includes(key) || key === presentation.initial_category))} {...{ presentation, field }} />
        <Choice label="Expanded on arrival" name="initially_expanded_markets" values={EXPANDED} {...{ presentation, field }} />
        <Choice label="Many-option markets" name="market_style" values={MARKET_STYLES} {...{ presentation, field }} />
        <div className={styles.inspectorReset}><span>Tab order</span>{field.overrides.has('category_order') ? <ResetButton name="category_order" field={field} /> : null}</div>
        <p className={styles.inspectorHint}>Drag tabs under Markets to reorder them.</p>
      </> : null}
      {section === 'event_probability' ? <p className={styles.inspectorHint}>Shows on the All Markets tab when a moneyline is posted.</p> : null}
    </div>
    {current && !REQUIRED.has(section) ? <div className={styles.panelFooter}>
      <Switch label="Visible on this page" checked={current.enabled} disabled={field.disabled} onChange={(checked) => field.setField('sections', presentation.sections.map((item) => item.type === section ? { ...item, enabled: checked } : item))} />
      {field.overrides.has('sections') ? <ResetButton name="sections" field={field} /> : null}
    </div> : field.overrides.has('sections') ? <div className={styles.panelFooter}><span className={styles.inspectorHint}>Section order</span><ResetButton name="sections" field={field} /></div> : null}
  </section>
}

function Choice({ label, name, values, presentation, field }) {
  return <Field label={label} labelExtra={field.overrides.has(name) ? <ResetButton name={name} field={field} /> : null}>
    <Select size="compact" disabled={field.disabled} value={presentation[name]} onChange={(change) => field.setField(name, change.target.value)}>
      {Object.entries(values).map(([key, text]) => <option key={key} value={key}>{text}</option>)}
    </Select>
  </Field>
}

function ResetButton({ name, field }) {
  return <Button size="compact" variant="ghost" title={field.resetLabel} aria-label={`${field.resetLabel}: ${name.replaceAll('_', ' ')}`} disabled={field.disabled} onClick={() => field.setField(name, undefined)}><RotateCcw size={12} aria-hidden="true" />Reset</Button>
}
